/**
 * Settings Panel Component
 * Model selection, generation params, and app preferences.
 * No API key management — keys are server-side in the Cloudflare Worker.
 */

import { store, updateSettings, setMemoryItems } from '../lib/appState.js';
import { providers } from '../providers/registry.js';
import { getMemoryItems, addMemoryItem, removeMemoryItem } from '../lib/memory.js';
import { formatTokens, formatCost, getUsage, resetUsage } from '../lib/usage.js';
import { showToast } from '../lib/toast.js';

function getCurrentTheme() {
  return localStorage.getItem('chorus_theme') || 'dark';
}

export function applyTheme(theme) {
  localStorage.setItem('chorus_theme', theme);
  const html = document.documentElement;
  if (theme === 'system') {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    html.setAttribute('data-theme', prefersDark ? 'dark' : 'light');
  } else {
    html.setAttribute('data-theme', theme);
  }
}

export function initTheme() {
  applyTheme(getCurrentTheme());
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (getCurrentTheme() === 'system') applyTheme('system');
  });
}

export function renderSettings() {
  const state = store.getState();
  const allProviders = providers.getConfigured();
  const active = providers.getActive();

  return `
    <div class="settings-overlay" data-action="close-settings">
      <div class="settings-panel" onclick="event.stopPropagation()">
        <div class="settings-header">
          <h2>Settings</h2>
          <button class="menu-btn" data-action="close-settings" aria-label="Close settings">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        </div>

        <div class="settings-body">
          <section class="settings-section">
            <h3>AI Provider</h3>
            <p class="settings-hint">API keys are managed server-side — no configuration needed.</p>
            
            <div class="settings-field">
              <label for="setting-provider">Provider</label>
              <select class="settings-select" id="setting-provider" data-setting-provider="true">
                ${allProviders.map(p => `
                  <option value="${p.id}" ${active?.providerId === p.id ? 'selected' : ''}>${p.name}</option>
                `).join('')}
              </select>
            </div>

            <div class="settings-field">
              <label for="setting-model">Model</label>
              <select class="settings-select" id="setting-model" data-setting-model="true">
                ${renderModelOptions(active?.providerId)}
              </select>
            </div>

            <div class="provider-status-list">
              ${allProviders.map(p => `
                <div class="provider-status-item">
                  <span class="provider-dot configured"></span>
                  <span>${p.name}</span>
                  <span class="provider-model-count">${p.models?.length || 0} models</span>
                </div>
              `).join('')}
            </div>
          </section>

          <section class="settings-section">
            <h3>Generation</h3>
            <div class="settings-field">
              <label for="setting-temp">Temperature</label>
              <div class="settings-range-row">
                <input type="range" id="setting-temp" min="0" max="2" step="0.1"
                  value="${state.settings.temperature}" data-setting="temperature">
                <span class="settings-range-value" id="temp-value">${state.settings.temperature}</span>
              </div>
            </div>
            <div class="settings-field">
              <label for="setting-tokens">Max Tokens</label>
              <div class="settings-range-row">
                <input type="range" id="setting-tokens" min="256" max="16384" step="256"
                  value="${state.settings.maxTokens}" data-setting="maxTokens">
                <span class="settings-range-value" id="tokens-value">${state.settings.maxTokens}</span>
              </div>
            </div>
            <div class="settings-field">
              <label class="settings-toggle-label">
                <span>Stream Responses</span>
                <input type="checkbox" ${state.settings.streamResponses ? 'checked' : ''}
                  data-setting="streamResponses">
                <span class="settings-toggle-switch"></span>
              </label>
            </div>
            <div class="settings-field">
              <label class="settings-toggle-label">
                <span>Smart Routing</span>
                <input type="checkbox" ${state.settings.smartRouting ? 'checked' : ''}
                  data-setting="smartRouting">
                <span class="settings-toggle-switch"></span>
              </label>
            </div>
            <div class="settings-field">
              <label class="settings-toggle-label">
                <span>Follow-up Suggestions</span>
                <input type="checkbox" ${state.settings.followUpSuggestions ? 'checked' : ''}
                  data-setting="followUpSuggestions">
                <span class="settings-toggle-switch"></span>
              </label>
            </div>
          </section>

          <section class="settings-section">
            <h3>Memory</h3>
            <p class="settings-hint">Custom instructions prepended to every conversation.</p>
            <div class="memory-items-list" id="memory-items">
              ${renderMemoryItems()}
            </div>
            <div class="memory-add-row">
              <input type="text" class="settings-input memory-input" id="memory-input" placeholder="e.g., I'm a software engineer...">
              <button class="settings-btn-sm" id="memory-add-btn">Add</button>
            </div>
          </section>

          <section class="settings-section">
            <h3>Appearance</h3>
            <div class="settings-field">
              <label for="setting-theme">Theme</label>
              <div class="theme-selector">
                <button class="theme-btn ${getCurrentTheme() === 'dark' ? 'active' : ''}" data-action="set-theme" data-theme="dark">Dark</button>
                <button class="theme-btn ${getCurrentTheme() === 'light' ? 'active' : ''}" data-action="set-theme" data-theme="light">Light</button>
                <button class="theme-btn ${getCurrentTheme() === 'system' ? 'active' : ''}" data-action="set-theme" data-theme="system">System</button>
              </div>
            </div>
          </section>

          <section class="settings-section">
            <h3>Usage</h3>
            ${renderUsageStats()}
          </section>

          <section class="settings-section">
            <h3>Export</h3>
            <div class="settings-actions">
              <button class="settings-btn" data-action="export-md">Current (.md)</button>
              <button class="settings-btn" data-action="export-json">Current (.json)</button>
              <button class="settings-btn" data-action="export-pdf">Print / PDF</button>
              <button class="settings-btn" data-action="copy-conversation">Copy to Clipboard</button>
            </div>
          </section>

          <section class="settings-section">
            <h3>Data</h3>
            <div class="settings-actions">
              <button class="settings-btn" data-action="export-data">Export Conversations</button>
              <button class="settings-btn danger" data-action="clear-data">Clear All Data</button>
            </div>
          </section>
          <section class="settings-section">
            <h3>Account</h3>
            <div class="settings-actions">
              <button class="settings-btn danger" data-action="logout">Sign Out</button>
            </div>
          </section>
        </div>
      </div>
    </div>
  `;
}

function renderModelOptions(providerId) {
  if (!providerId) return '<option>No provider selected</option>';
  const provider = providers.get(providerId);
  if (!provider || !provider.models) return '<option>No models</option>';

  const active = providers.getActive();
  return provider.models.map(m => `
    <option value="${m.id}" ${active?.model === m.id ? 'selected' : ''}>${m.name || m.id}</option>
  `).join('');
}

function renderMemoryItems() {
  const items = getMemoryItems();
  if (items.length === 0) {
    return '<div class="memory-empty">No memory items. Add instructions below.</div>';
  }
  return items.map(item => `
    <div class="memory-chip" data-memory-id="${item.id}">
      <span class="memory-chip-text">${escapeSettingsHtml(item.content)}</span>
      <button class="memory-chip-remove" data-remove-memory="${item.id}">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
      </button>
    </div>
  `).join('');
}

function renderUsageStats() {
  const usage = getUsage();
  const total = usage.total;
  return `
    <div class="usage-stats">
      <div class="usage-stat">
        <span class="usage-stat-label">Total Messages</span>
        <span class="usage-stat-value">${total.messageCount}</span>
      </div>
      <div class="usage-stat">
        <span class="usage-stat-label">Input Tokens</span>
        <span class="usage-stat-value">${formatTokens(total.inputTokens)}</span>
      </div>
      <div class="usage-stat">
        <span class="usage-stat-label">Output Tokens</span>
        <span class="usage-stat-value">${formatTokens(total.outputTokens)}</span>
      </div>
      <div class="usage-stat">
        <span class="usage-stat-label">Est. Total Cost</span>
        <span class="usage-stat-value">${formatCost(total.cost)}</span>
      </div>
    </div>
    <button class="settings-btn-sm" id="reset-usage-btn" style="margin-top: var(--space-3)">Reset Usage</button>
  `;
}

function escapeSettingsHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

export function bindSettingsEvents(container) {
  const panel = container.querySelector('.settings-panel');
  if (!panel) return;

  // Provider selection change
  const providerSelect = panel.querySelector('[data-setting-provider]');
  const modelSelect = panel.querySelector('[data-setting-model]');

  providerSelect?.addEventListener('change', () => {
    const providerId = providerSelect.value;
    const provider = providers.get(providerId);
    if (provider && modelSelect) {
      modelSelect.innerHTML = renderModelOptions(providerId);
      providers.setActive(providerId, provider.model);
    }
  });

  modelSelect?.addEventListener('change', () => {
    const providerId = providerSelect?.value;
    const model = modelSelect.value;
    if (providerId) {
      providers.setActive(providerId, model);
    }
  });

  // Settings changes (temperature, maxTokens, etc.)
  panel.addEventListener('change', (e) => {
    const settingKey = e.target.dataset.setting;
    if (settingKey) {
      let value = e.target.type === 'checkbox' ? e.target.checked : Number(e.target.value);
      updateSettings({ [settingKey]: value });
    }
  });

  // Range input live display
  panel.addEventListener('input', (e) => {
    if (e.target.type === 'range') {
      const settingKey = e.target.dataset.setting;
      if (settingKey === 'temperature') {
        panel.querySelector('#temp-value').textContent = e.target.value;
      } else if (settingKey === 'maxTokens') {
        panel.querySelector('#tokens-value').textContent = e.target.value;
      }
    }
  });

  // Theme selector
  panel.querySelectorAll('[data-action="set-theme"]').forEach(btn => {
    btn.addEventListener('click', () => {
      applyTheme(btn.dataset.theme);
      panel.querySelectorAll('.theme-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
    });
  });

  // Memory: add item
  const memoryInput = panel.querySelector('#memory-input');
  const memoryAddBtn = panel.querySelector('#memory-add-btn');
  if (memoryInput && memoryAddBtn) {
    const doAdd = async () => {
      const value = memoryInput.value.trim();
      if (!value) return;
      await addMemoryItem(value);
      memoryInput.value = '';
      setMemoryItems(getMemoryItems());
      const list = panel.querySelector('#memory-items');
      if (list) list.innerHTML = renderMemoryItems();
      // Rebind remove buttons
      bindMemoryRemoveButtons(panel);
      showToast('Memory item added', 'success');
    };
    memoryAddBtn.addEventListener('click', doAdd);
    memoryInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') doAdd();
    });
  }
  bindMemoryRemoveButtons(panel);

  // Usage: reset button
  const resetBtn = panel.querySelector('#reset-usage-btn');
  if (resetBtn) {
    resetBtn.addEventListener('click', async () => {
      await resetUsage();
      showToast('Usage data reset', 'info');
      // Re-render usage stats
      const usageSection = resetBtn.closest('.settings-section');
      if (usageSection) {
        const h3 = usageSection.querySelector('h3');
        usageSection.innerHTML = '';
        usageSection.appendChild(h3);
        usageSection.insertAdjacentHTML('beforeend', renderUsageStats());
      }
    });
  }
}

function bindMemoryRemoveButtons(panel) {
  panel.querySelectorAll('[data-remove-memory]').forEach(btn => {
    btn.addEventListener('click', async () => {
      const id = btn.dataset.removeMemory;
      await removeMemoryItem(id);
      setMemoryItems(getMemoryItems());
      const list = panel.querySelector('#memory-items');
      if (list) list.innerHTML = renderMemoryItems();
      bindMemoryRemoveButtons(panel);
      showToast('Memory item removed', 'info');
    });
  });
}
